import { useOrganizationId } from 'application/contexts/SiteContext';
import { usePageContext } from 'application/contexts/PageContext';
import { personToJsonLd, imageToJsonLd, findEventStartDate, findPageHeaderHero } from 'helpers/jsonLd';
import { JsonLdScript } from './JsonLdScript';

// articlePage's "heading" is body content, not the title - mainHeading is the real title.
type ArticleLikeProperties = {
	mainHeading?: string;
	newsHeading?: string;
	teaser?: string;
	newsTeaser?: string;
	image?: Content.Image;
	featuredImage?: Content.Image;
	authors?: Content.Person[];
	newsAuthors?: Content.Person[];
	hero?: Content.HeroCard;
	pageHeader?: Content.HeroCard[];
	layoutType?: 'Article' | 'Event';
	location?: string;
	articleInfo?: Content.ArticleInfoColumn[];
	kicker?: string;
	tags?: string[];
};

// Rendered only via PageSchemaJsonLd's dispatch - no need to re-check documentType here.
export const ArticleJsonLd: React.FC = () => {
	const pageContext = usePageContext();
	const organizationId = useOrganizationId();

	const properties = pageContext.content as unknown as ArticleLikeProperties;
	const headline = properties?.mainHeading || properties?.newsHeading;
	if (!headline) return null;

	const heroImage = properties?.hero?.content?.properties;
	const pageHeaderHero = findPageHeaderHero(properties?.pageHeader);
	const image =
		properties?.featuredImage ||
		properties?.image ||
		heroImage?.featuredImage ||
		heroImage?.image ||
		pageHeaderHero?.featuredImage ||
		pageHeaderHero?.image;
	const authors = properties?.authors || properties?.newsAuthors || [];

	const isEventLayout = pageContext.documentType === 'articlePage' && properties?.layoutType === 'Event';
	const eventStartDate = isEventLayout ? findEventStartDate(properties?.articleInfo) : undefined;

	// An Event without a startDate is invalid - fall back to BlogPosting rather than ship it broken.
	const article = eventStartDate
		? {
				'@context': 'https://schema.org',
				'@type': 'Event',
				name: headline,
				description: properties?.teaser || undefined,
				startDate: eventStartDate,
				location: properties?.location ? { '@type': 'Place', name: properties.location } : undefined,
				image: imageToJsonLd(image),
				url: pageContext.path,
				organizer: organizationId ? { '@id': organizationId } : undefined,
		  }
		: {
				'@context': 'https://schema.org',
				'@type': pageContext.documentType === 'articlePage' ? 'BlogPosting' : 'NewsArticle',
				headline,
				description: properties?.teaser || properties?.newsTeaser || undefined,
				mainEntityOfPage: pageContext.path,
				url: pageContext.path,
				datePublished: pageContext.createDate || undefined,
				dateModified: pageContext.updateDate || pageContext.createDate || undefined,
				inLanguage: pageContext.culture || undefined,
				image: imageToJsonLd(image),
				author: authors.map((author) => personToJsonLd(author, organizationId)).filter(Boolean),
				publisher: organizationId ? { '@id': organizationId } : undefined,
				articleSection: properties?.kicker || undefined,
				keywords: properties?.tags?.length ? properties.tags : undefined,
				isAccessibleForFree: true,
		  };

	return <JsonLdScript id="article-jsonld" data={article} />;
};
